import { Option, Schema } from "effect";
import { schemaParser, schemaDecoder } from "./schema-compat.ts";

import { developmentIdentifier } from "./development-identifiers.ts";
import { stationPath } from "./station-path.ts";

export const ReviewDiffSide = /* @__PURE__ */ (() => Schema.Literals(["base", "candidate"]))();
export type ReviewDiffSide = typeof ReviewDiffSide.Type;
export const ReviewDiffPresentationMode = /* @__PURE__ */ (() => Schema.Literals(["unified", "side-by-side"]))();
export type ReviewDiffPresentationMode = typeof ReviewDiffPresentationMode.Type;

export const MAX_REVIEW_DIFF_FILE_BYTES = 5 * 1024 * 1024;
const reviewDiffEncoder = new TextEncoder();

export const ReviewDiffModelIdentityInput = /* @__PURE__ */ (() => Schema.Struct({
  side: ReviewDiffSide,
  candidateId: Schema.String,
  snapshotTreeId: Schema.String,
  workspaceId: Schema.String,
  path: Schema.String,
}))();
export type ReviewDiffModelIdentityInput = typeof ReviewDiffModelIdentityInput.Type;

/** Reject oversized diff text before Monaco or its models are loaded. */
export const ReviewDiffContents = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isMaxLength(MAX_REVIEW_DIFF_FILE_BYTES),
  Schema.makeFilter(value => reviewDiffEncoder.encode(value).byteLength <= MAX_REVIEW_DIFF_FILE_BYTES)
))();
export const boundedReviewDiffContents = /* @__PURE__ */ (() => schemaParser(ReviewDiffContents, () => new TypeError("Review diff contents must be bounded UTF-8 text.")))();
const presentationMode = /* @__PURE__ */ (() => schemaDecoder(ReviewDiffPresentationMode, () => new TypeError("Review diff presentation mode is invalid.")))();
const diffSide = /* @__PURE__ */ (() => schemaDecoder(ReviewDiffSide, () => new TypeError("Review diff side is invalid.")))();

export function reviewDiffAccessibility(
  mode: ReviewDiffPresentationMode,
  pathValue: string
): Readonly<{ label: string; instructions: string }> {
  const path = stationPath(pathValue);
  presentationMode.parse(mode);
  return Object.freeze({
    label:
      mode === "unified"
        ? `Unified immutable candidate diff for ${path}`
        : `Side-by-side immutable candidate diff for ${path}`,
    instructions:
      mode === "unified"
        ? "Press F7 or Shift+F7 to move through changed hunks in source order. The accessible diff viewer labels removed and added lines."
        : "Press F7 or Shift+F7 to move through changed hunks. The accessible diff viewer identifies the original and candidate source."
  });
}

const unicodeScalarPath = /* @__PURE__ */ (() => schemaDecoder(Schema.String.check(
  Schema.isPattern(/^[\u0000-\uD7FF\uE000-\u{10FFFF}]*$/u)
), () => new TypeError("Review diff path must contain only valid Unicode scalar values.")))();

/** Build a collision-resistant immutable Monaco URI with no live file scheme. */
export function reviewDiffModelUri(
  input: ReviewDiffModelIdentityInput
): string {
  diffSide.parse(input.side);
  const candidateId = developmentIdentifier("candidate", input.candidateId);
  const treeId = developmentIdentifier("tree", input.snapshotTreeId);
  const workspaceId = developmentIdentifier("workspace", input.workspaceId);
  const path = stationPath(input.path);
  // encodeURIComponent throws for lone UTF-16 surrogates. Reject them
  // explicitly so two malformed paths can never collapse to replacement text.
  unicodeScalarPath.parse(path);
  const encodedPath = path
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return `workerstation-review-${input.side}://snapshot/${encodeURIComponent(
    candidateId
  )}/${encodeURIComponent(treeId)}/${encodeURIComponent(
    workspaceId
  )}/${encodedPath}`;
}

/** Validate and clamp a requested reveal line against the immutable model. */
const RevealLine = /* @__PURE__ */ (() => Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)))();
const revealLine = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.Struct({ line: RevealLine, lineCount: RevealLine })))();
export const boundedReviewDiffLine = (line: unknown, lineCount: unknown): number | undefined =>
  Option.match(revealLine({ line, lineCount }), {
    onSome: value => Math.min(value.line, value.lineCount),
    onNone: () => undefined
  });

export function reviewDiffLanguage(pathValue: string): string | undefined {
  const path = stationPath(pathValue).toLowerCase();
  const extension = path.includes(".") ? path.split(".").at(-1) : undefined;
  switch (extension) {
    case "ts":
    case "mts":
    case "cts":
      return "typescript";
    case "tsx":
      return "typescript";
    case "js":
    case "mjs":
    case "cjs":
    case "jsx":
      return "javascript";
    case "json":
      return "json";
    case "css":
      return "css";
    case "html":
      return "html";
    case "md":
    case "mdx":
      return "markdown";
    case "sql":
      return "sql";
    case "yaml":
    case "yml":
      return "yaml";
    default:
      return undefined;
  }
}
